/* eslint-disable import/no-extraneous-dependencies */
/*  eslint-disable */
import { useState, useEffect } from "react";
import { Link as MuiLink } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";

import Box from "src/components/shared/atoms/box";
import TextField from "src/components/shared/atoms/textField/TextField";
import AuthButton from "src/components/pages/auth/shared/button/AuthButton";
import { Link, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  SignInErrorsProps,
  FormValues,
} from "src/components/pages/auth/login/form/Types";
import { useLazyQuery, useMutation } from "@apollo/client";
import LOGIN_MUTATION from "src/graphql/mutation/auth/login";
import GET_LOGIN_METHOD from "src/graphql/query/auth/getLoginMethod";
import setAuthDataInLocalStorage from "src/utils/auth/LocalStorage";
import useStyles from "./Styles";

const Form = () => {
  const { classes } = useStyles();
  const [email, setEmail] = useState<string>("");
  const [signInErrors, setSignInErrors] = useState<SignInErrorsProps | null>(
    null,
  );

  const navigate = useNavigate();
  const [
    getLogin,
    {
      loading: loginQueryLoading,
      error: loginQueryError,
      data: loginQueryResult,
    },
  ] = useMutation(LOGIN_MUTATION);

  const [getLoginMethod] = useLazyQuery(GET_LOGIN_METHOD);

  const handleEmailBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    formik: any,
  ) => {
    formik.handleBlur(e);
    const emailValue = formik.values.email;
    if (emailValue && !formik.errors.email) {
      try {
        const { data } = await getLoginMethod({
          variables: { email: emailValue },
        });

        if (data?.getLoginMethod?.success) {
          const { hasPassword, loginMethod } = data.getLoginMethod;
          if (!hasPassword && loginMethod !== "Password") {
            setSignInErrors({
              email: "",
              password: `login with ${loginMethod} or reset your password`,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching login method:", error);
      }
    }
  };

  useEffect(() => {
    if (loginQueryResult && loginQueryResult.login) {
      const isSet = setAuthDataInLocalStorage(loginQueryResult.login);
      if (isSet) navigate("/");
    }
  }, [loginQueryResult, navigate]);

  useEffect(() => {
    if (loginQueryError) {
      if (
        loginQueryError.message === "Failed to fetch" ||
        loginQueryError.message ===
          "An unexpected error occurred. Please try again later."
      ) {
        enqueueSnackbar("Something went wrong. Try again.", {
          variant: "error",
        });
        return;
      }

      // const backendErrors = JSON.parse(loginQueryError?.message ?? "");
      let backendErrors;

      // try {
      //   backendErrors = JSON.parse(loginQueryError.message);
      // } catch (err) {
      //   console.error("Error message is not JSON:", loginQueryError.message);

      //   enqueueSnackbar(loginQueryError.message, { variant: "error" });
      //   return;
      // }

      try {
        backendErrors = JSON.parse(loginQueryError.message);
      } catch (err) {
        // 🔥 NEW: check for GraphQL error inside networkError
        const networkErrors = (loginQueryError.networkError as any)?.result
          ?.errors;

        if (networkErrors?.length) {
          backendErrors = {
            message: networkErrors[0].message,
          };
        } else {
          console.error("Error message is not JSON:", loginQueryError.message);
          enqueueSnackbar(loginQueryError.message, { variant: "error" });
          return;
        }
      }

      setSignInErrors(backendErrors);
      if (backendErrors.message) {
        enqueueSnackbar(backendErrors.message, { variant: "error" });

        const isIpRateLimitError = backendErrors.message
          .toLowerCase()
          .includes("too many authentication attempts from this ip");

        // 🚫 Stop here for this specific error
        if (isIpRateLimitError) {
          return;
        }

        if (
          backendErrors.message.includes(
            "Too many attempts. Try again in 5 minutes",
          )
        ) {
          navigate("/confirm", { state: { email, stopTimer: true } });
        } else if (
          backendErrors.message !==
          "We could not send a confirmation email. Please try again later"
        ) {
          navigate("/confirm", { state: { email } });
        }
      }
    }
  }, [loginQueryError, navigate, email]);

  // validation schema using Yup
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(3, "Password must be at least 3 characters")
      .required("Password is required"),
  });
  // if login mutation sends back the result

  const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
    try {
      setEmail(values.email);
      await getLogin({
        variables: {
          email: values.email,
          password: values.password,
        },
      });
    } catch (err: any) {
      return;
    }
    setSubmitting(false);
  };

  // const handleOnBlur = async (
  //   e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  //   formik: any,
  // ) => {
  //   const fieldName = e.target.name;

  //   // First mark field as touched
  //   formik.setFieldTouched(fieldName, true, false);

  //   // 🚫 Stop if password is empty
  //   if (!formik.values.password) return;

  //   // Then run Yup validation schema manually for that field
  //   try {
  //     await formik.validateField(fieldName);
  //     const email = formik.values.email;

  //     // If no error after validation, run the logic
  //     if (!formik.errors.email) {
  //       setEmail(email);

  //       await getLogin({
  //         variables: {
  //           email,
  //           password: formik.values.password,
  //         },
  //       });
  //     }
  //   } catch (err) {
  //     // Yup validation will already handle error display via Formik
  //     console.error("Validation error:", err);
  //   }
  // };

  return (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik: any) => (
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            width: "100%",
          }}
        >
          <Box className={classes.marginTop}>
            <TextField
              id="email"
              label="Email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={(e) => handleEmailBlur(e, formik)}
              helperText={
                (signInErrors && signInErrors.email) ||
                (formik.touched.email && formik.errors.email)
              }
            />
          </Box>
          <Box className={classes.marginTop}>
            <TextField
              id="password"
              label="Password"
              name="password"
              typeField="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              helperText={
                (signInErrors && signInErrors.password) ||
                (formik.touched.password && formik.errors.password)
              }
            />
          </Box>
          <Box className={classes.label}>
            <MuiLink>
              <Link className={classes.link} to="/forgetpassword">
                Forgot password?
              </Link>
            </MuiLink>
          </Box>
          <AuthButton text="Sign In" loading={loginQueryLoading} />
        </Box>
      )}
    </Formik>
  );
};

export default Form;
